import { ThemedLogo } from './themed-logo';
import { cn } from '@/lib/utils';

interface CareOSLoaderProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  label?: string;
  fullScreen?: boolean;
  className?: string;
}

const sizeMap = {
  sm: 'h-8 w-8',
  md: 'h-12 w-12',
  lg: 'h-20 w-20',
  xl: 'h-28 w-28',
};

/**
 * CareOS branded loader: pulsing logo with a teal glow halo.
 * Respects prefers-reduced-motion automatically via Tailwind's motion-safe variants.
 */
export function CareOSLoader({
  size = 'md',
  label,
  fullScreen = false,
  className,
}: CareOSLoaderProps) {
  const content = (
    <div className={cn('flex flex-col items-center justify-center gap-4', className)}>
      <div className="relative flex items-center justify-center">
        {/* Glow halo */}
        <span
          aria-hidden="true"
          className={cn(
            'absolute inset-0 rounded-full bg-primary/20 blur-xl motion-safe:animate-careos-glow',
            sizeMap[size]
          )}
        />
        {/* Logo */}
        <span className={cn('relative motion-safe:animate-careos-pulse', sizeMap[size])}>
          <ThemedLogo className="h-full w-full object-contain" />
        </span>
      </div>
      {label && (
        <p className="text-sm text-muted-foreground animate-pulse">{label}</p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
      >
        {content}
      </div>
    );
  }

  return (
    <div role="status" aria-live="polite" aria-busy="true">
      {content}
    </div>
  );
}
